// =============================================================================
// MfaGate — trusted-device MFA for manager and above (T-MFA, 28 Aug 2026)
// =============================================================================
// James's policy, in his words: "make it required if the login has not been in
// for over 24hrs because I use this all day and open it in a few browsers, on
// my phone... MFA linked to the account not being used, and then a hard reset
// every 7 days so once a week if regular use."
//
// So: authenticator-app codes (Supabase TOTP - no SMS), for MANAGER AND ABOVE
// only. Field logins never see this. Each browser/phone is a trusted device:
//
//   * no code enrolled yet          -> one-time setup screen (QR + first code)
//   * fresh session on a device     -> code
//   * device idle for 24+ hours     -> code
//   * 7 days since the last code    -> code, no matter what
//   * otherwise                     -> invisible. Use it all day, no prompts.
//
// The stamps live in this browser's localStorage (per device by definition -
// that is the point). The hard security floor is NOT the stamps: it is that a
// session only reaches AAL2 by passing a real TOTP verify, and the worker
// refuses money/admin routes below AAL2. Wiping localStorage just means being
// asked for a code again.
//
// Lost phone: a director clears the person's factors (Reset MFA on the
// employee card -> POST /admin/mfa-reset) and this gate re-runs setup at
// their next login.
// =============================================================================

import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Smartphone, LogOut, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuthStore, hasRole, type CoretelUser } from "@/lib/auth";
import { Button, Spinner } from "@/components/ui";

const IDLE_MS   = 24 * 60 * 60 * 1000;      // 24 hours untouched -> re-verify
const HARD_MS   = 7  * 24 * 60 * 60 * 1000; // 7 days regardless  -> re-verify
const SEEN_EVERY_MS = 5 * 60 * 1000;        // how often activity bumps the stamp

type GateState = "checking" | "clear" | "enrol" | "challenge";

function storageKey(authUserId: string) {
  return `ct_mfa_${authUserId}`;
}
function readStamps(authUserId: string): { v: number; s: number } {
  try {
    const raw = window.localStorage.getItem(storageKey(authUserId));
    if (raw) {
      const p = JSON.parse(raw);
      if (typeof p.v === "number" && typeof p.s === "number") return p;
    }
  } catch { /* storage blocked - treat as never verified */ }
  return { v: 0, s: 0 };
}
function writeStamps(authUserId: string, v: number, s: number) {
  try { window.localStorage.setItem(storageKey(authUserId), JSON.stringify({ v, s })); }
  catch { /* not fatal - just means a code sooner */ }
}

export default function MfaGate({ user }: { user: CoretelUser }) {
  const signOut = useAuthStore(st => st.signOut);
  const required = hasRole(user, "manager");

  const [state, setState]       = useState<GateState>(required ? "checking" : "clear");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qr, setQr]             = useState<string | null>(null);
  const [secret, setSecret]     = useState<string | null>(null);
  const [code, setCode]         = useState("");
  const [err, setErr]           = useState<string | null>(null);
  const [busy, setBusy]         = useState(false);
  const enrolling = useRef(false);
  const codeRef = useRef<HTMLInputElement | null>(null);

  // ---- the policy check ----------------------------------------------------
  async function evaluate() {
    if (!required) { setState("clear"); return; }
    try {
      const { data: factors, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      const verified = (factors?.totp ?? []).find(f => f.status === "verified");

      if (!verified) { setState("enrol"); return; }
      setFactorId(verified.id);

      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const stamps = readStamps(user.auth_user_id);
      const now = Date.now();

      const needsCode =
        aal?.currentLevel !== "aal2"          // fresh session on this device
        || now - stamps.v > HARD_MS           // weekly hard reset
        || now - stamps.s > IDLE_MS;          // device untouched for a day

      if (needsCode) { setState("challenge"); return; }
      writeStamps(user.auth_user_id, stamps.v, now);   // bump last-seen
      setState("clear");
    } catch (e) {
      // If the check itself fails (offline, auth hiccup) don't brick the app -
      // the worker still enforces AAL2 on anything that matters.
      console.warn("[mfa] policy check failed", e);
      setState("clear");
    }
  }

  useEffect(() => { evaluate(); /* on mount + when user changes */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.auth_user_id, required]);

  // Re-check when the tab comes back to life (this is how "idle 24h" fires),
  // and keep the last-seen stamp fresh while they work.
  useEffect(() => {
    if (!required) return;
    let lastBump = 0;
    function onActivity() {
      const now = Date.now();
      if (now - lastBump < SEEN_EVERY_MS) return;
      lastBump = now;
      const st = readStamps(user.auth_user_id);
      // Only bump while trusted - activity on the challenge screen must not
      // count as "still using it".
      if (Date.now() - st.v <= HARD_MS) writeStamps(user.auth_user_id, st.v, now);
    }
    function onVisible() { if (document.visibilityState === "visible") evaluate(); }
    window.addEventListener("pointerdown", onActivity);
    window.addEventListener("keydown", onActivity);
    document.addEventListener("visibilitychange", onVisible);
    const iv = window.setInterval(evaluate, 30 * 60 * 1000);   // belt-and-braces
    return () => {
      window.removeEventListener("pointerdown", onActivity);
      window.removeEventListener("keydown", onActivity);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(iv);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required, user.auth_user_id]);

  // ---- enrolment (one-time setup) ------------------------------------------
  useEffect(() => {
    if (state !== "enrol" || enrolling.current) return;
    enrolling.current = true;
    (async () => {
      setErr(null);
      try {
        // A half-finished earlier attempt leaves an unverified factor that
        // blocks a fresh enrol with the same name - clear those first.
        const { data: factors } = await supabase.auth.mfa.listFactors();
        for (const f of (factors?.totp ?? []).filter(f => f.status !== "verified")) {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
        // issuer = what the authenticator app SHOWS as the entry name. Left
        // unset it falls back to the site URL - James's app said "localhost".
        // Staging and production are separate logins, so a phone legitimately
        // holds one entry for each - name them so they can't be mixed up.
        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: "totp",
          issuer: import.meta.env.DEV ? "Coretel Ops STAGING" : "Coretel Ops",
          friendlyName: "Authenticator app",
        });
        if (error) throw error;
        setFactorId(data.id);
        setQr(data.totp.qr_code);
        setSecret(data.totp.secret);
      } catch (e: any) {
        setErr(e?.message ?? String(e));
      } finally {
        enrolling.current = false;
      }
    })();
  }, [state]);

  // ---- submit a code (both setup and everyday challenge) -------------------
  async function submitCode() {
    if (!factorId || code.trim().length < 6) return;
    setBusy(true);
    setErr(null);
    try {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
      if (chErr) throw chErr;
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId, challengeId: ch.id, code: code.trim(),
      });
      if (vErr) throw vErr;
      const now = Date.now();
      writeStamps(user.auth_user_id, now, now);
      setCode("");
      setQr(null); setSecret(null);
      setState("clear");
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setErr(/invalid/i.test(msg)
        ? "That code didn't match — check the app and try the current 6 digits."
        : msg);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (state === "challenge" || (state === "enrol" && qr)) codeRef.current?.focus();
  }, [state, qr]);

  if (state === "clear") return null;

  // ---- the gate ------------------------------------------------------------
  return (
    <div className="fixed inset-0 z-[100] bg-ink flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg border border-line bg-ink-card shadow-xl p-6">
        <div className="flex items-center gap-2.5 mb-1">
          <ShieldCheck size={20} className="text-accent" />
          <h2 className="text-[16px] font-semibold text-text">
            {state === "checking" ? "Checking your security…"
              : state === "enrol" ? "Secure your account"
              : "Enter your security code"}
          </h2>
        </div>

        {state === "checking" && (
          <div className="py-6 flex justify-center"><Spinner /></div>
        )}

        {state === "enrol" && (
          <div className="space-y-3">
            <p className="text-[12px] text-text-muted">
              Your login can approve money and see people's details, so it needs a
              6-digit code as well as a password. One-time setup, about two minutes:
            </p>
            <ol className="text-[12px] text-text-muted list-decimal pl-5 space-y-1">
              <li>Open an authenticator app on your phone — Google Authenticator,
                  Microsoft Authenticator or 1Password all work.</li>
              <li>Scan this QR code (or type the key below it).</li>
              <li>Enter the 6-digit code the app shows.</li>
            </ol>
            {qr ? (
              <div className="flex flex-col items-center gap-2 py-2">
                <img src={qr} alt="Scan with your authenticator app"
                  className="w-44 h-44 bg-white rounded p-2" />
                {secret && (
                  <div className="text-[10px] text-text-dim font-mono break-all text-center"
                    title="Type this into the app if you can't scan">
                    {secret}
                  </div>
                )}
              </div>
            ) : !err ? (
              <div className="py-6 flex justify-center"><Spinner /></div>
            ) : null}
          </div>
        )}

        {state === "challenge" && (
          <p className="text-[12px] text-text-muted mb-3 flex items-start gap-2">
            <Smartphone size={14} className="flex-shrink-0 mt-0.5 text-text-dim" />
            <span>
              This device hasn't been checked for a while — open your authenticator
              app and enter the current 6-digit code for Coretel Ops. Regular use
              means you'll only see this about once a week per device.
            </span>
          </p>
        )}

        {(state === "challenge" || (state === "enrol" && qr)) && (
          <div className="mt-3 space-y-3">
            <input
              ref={codeRef}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={e => { if (e.key === "Enter") submitCode(); }}
              inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
              className="w-full bg-ink border border-line rounded px-3 py-2.5 text-center
                         text-[20px] tracking-[0.4em] font-mono text-text
                         focus:outline-none focus:border-accent"
            />
            {err && <div className="text-[11px] text-danger">{err}</div>}
            <Button variant="primary" className="w-full" disabled={busy || code.length < 6}
              onClick={submitCode} icon={busy ? RefreshCw : ShieldCheck}>
              {busy ? "Checking…" : state === "enrol" ? "Finish setup" : "Unlock"}
            </Button>
          </div>
        )}

        {err && state === "enrol" && !qr && (
          <div className="text-[11px] text-danger mt-2">{err}</div>
        )}

        <div className="mt-4 pt-3 border-t border-line/50 flex items-center justify-between">
          <span className="text-[10px] text-text-dim">
            {state === "enrol"
              ? "Phone problems? Ask a director to reset your MFA."
              : "New phone or lost it? A director can reset your MFA."}
          </span>
          <button type="button" onClick={() => signOut()}
            className="text-[11px] text-text-muted hover:text-text inline-flex items-center gap-1">
            <LogOut size={11} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
